import { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { CheckCircle, XCircle, Loader2, ShieldCheck, Eye, FileText, Coins, Edit2, History, Upload, Trash2, User, Car, Camera, IdCard } from "lucide-react";
import { getAuthToken } from "../../lib/api";
import api, { resolveImageUrl } from "../../lib/api";
import TFTHistory from "../TFTHistory";
import { EditVehicleDialog } from "../driver/DriverForms";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function getAuthImgUrl(docPath) {
  if (!docPath) return null;
  const token = getAuthToken();
  return `${BACKEND_URL}/api/files/${docPath}?token=${token}`;
}

export function DocImage({ label, docPath }) {
  const [error, setError] = useState(false);
  if (!docPath) return (
    <div className="border-2 border-dashed border-[#CBD5E1] rounded-lg p-6 text-center">
      <FileText className="w-6 h-6 text-[#CBD5E1] mx-auto mb-1" />
      <p className="text-xs text-[#64748B]">{label} - Non fourni</p>
    </div>
  );
  return (
    <div className="border border-[#CBD5E1] rounded-lg overflow-hidden">
      <p className="text-xs font-medium text-[#64748B] px-2 py-1 bg-[#F8FAFC] border-b border-[#CBD5E1]">{label}</p>
      {error ? (
        <div className="p-4 text-center"><FileText className="w-6 h-6 text-[#CBD5E1] mx-auto mb-1" /><p className="text-xs text-[#64748B]">Image non disponible</p></div>
      ) : (
        <img src={getAuthImgUrl(docPath)} alt={label} className="w-full h-32 object-cover" onError={() => setError(true)} />
      )}
    </div>
  );
}

export function EditableDocCard({ label, docPath, driverId, documentType, onChange }) {
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !driverId) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("document_type", documentType);
      form.append("file", f);
      await api.post(`/admin/drivers/${driverId}/document`, form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Echec upload");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!docPath || !driverId) return;
    if (!window.confirm(`Supprimer "${label}" ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/drivers/${driverId}/document/${documentType}`);
      onChange?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Echec suppression");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#CBD5E1] rounded-lg overflow-hidden" data-testid={`editable-doc-${documentType}`}>
      <div className="flex items-center justify-between px-2 py-1 bg-[#F8FAFC] border-b border-[#CBD5E1]">
        <p className="text-xs font-medium text-[#64748B]">{label}</p>
        <div className="flex items-center gap-1">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid={`upload-input-${documentType}`} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="text-[10px] font-semibold text-[#0A2540] hover:text-[#FACC15] flex items-center gap-1 disabled:opacity-50"
            data-testid={`replace-doc-${documentType}`}
            title={docPath ? "Remplacer" : "Ajouter"}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {docPath ? "Remplacer" : "Ajouter"}
          </button>
          {docPath && (
            <button
              onClick={handleDelete}
              disabled={busy}
              className="text-[10px] font-semibold text-[#EF4444] hover:text-[#B91C1C] flex items-center gap-1 disabled:opacity-50 ml-2"
              data-testid={`delete-doc-${documentType}`}
            >
              <Trash2 className="w-3 h-3" /> Suppr.
            </button>
          )}
        </div>
      </div>
      {!docPath ? (
        <div className="p-6 text-center">
          <FileText className="w-6 h-6 text-[#CBD5E1] mx-auto mb-1" />
          <p className="text-xs text-[#64748B]">Aucun document</p>
        </div>
      ) : error ? (
        <div className="p-4 text-center"><FileText className="w-6 h-6 text-[#CBD5E1] mx-auto mb-1" /><p className="text-xs text-[#64748B]">Image non disponible</p></div>
      ) : (
        <img src={getAuthImgUrl(docPath)} alt={label} className="w-full h-32 object-cover" onError={() => setError(true)} />
      )}
    </div>
  );
}

export function InfoCard({ label, value }) {
  return (
    <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#CBD5E1]">
      <p className="text-xs text-[#64748B]">{label}</p>
      <p className="font-medium text-[#0A2540]">{value}</p>
    </div>
  );
}

export function getStatusBadge(status) {
  if (status === "approved") return { text: "Approuve", cls: "bg-[#10B981]/10 text-[#10B981]" };
  if (status === "pending") return { text: "En attente", cls: "bg-[#FACC15]/20 text-[#EAB308]" };
  return { text: "Refuse", cls: "bg-[#EF4444]/10 text-[#EF4444]" };
}

export function DocViewerDialog({ driver, onClose, onValidate, rejectReason, setRejectReason, actionLoading, onRefresh }) {
  return (
    <Dialog open={!!driver} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="driver-docs-modal">
        <DialogHeader>
          <DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Documents de {driver?.name}</DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Consultation et gestion des documents du chauffeur
          </DialogDescription>
        </DialogHeader>
        {driver && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <InfoCard label="Email" value={driver.email} />
              <InfoCard label="Telephone" value={driver.phone || "N/A"} />
              <InfoCard label="Numero CNI" value={driver.cni_number || "N/A"} />
              <InfoCard label="Numero Permis" value={driver.license_number || "N/A"} />
              <InfoCard label="Credit TFT" value={`${(driver.tft_balance || 0).toLocaleString()} TFT`} />
            </div>
            {driver.profile_photo && (
              <div>
                <h3 className="text-sm font-semibold text-[#0A2540] mb-2">Photo de profil</h3>
                <img src={getAuthImgUrl(driver.profile_photo)} alt="Profil" className="w-24 h-24 rounded-xl object-cover border border-[#CBD5E1]" />
              </div>
            )}
            <div>
              <h3 className="text-sm font-semibold text-[#0A2540] mb-2">CNI</h3>
              <div className="grid grid-cols-2 gap-3">
                <EditableDocCard label="Recto" docPath={driver?.driver_documents?.cni_front} driverId={driver.id} documentType="cni_front" onChange={onRefresh} />
                <EditableDocCard label="Verso" docPath={driver?.driver_documents?.cni_back} driverId={driver.id} documentType="cni_back" onChange={onRefresh} />
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#0A2540] mb-2">Permis de Conduire</h3>
              <div className="grid grid-cols-2 gap-3">
                <EditableDocCard label="Recto" docPath={driver?.driver_documents?.license_front} driverId={driver.id} documentType="license_front" onChange={onRefresh} />
                <EditableDocCard label="Verso" docPath={driver?.driver_documents?.license_back} driverId={driver.id} documentType="license_back" onChange={onRefresh} />
              </div>
            </div>
            {driver.driver_status !== "approved" ? (
              <div className="border-t border-[#CBD5E1] pt-4 space-y-3">
                <Button onClick={() => onValidate(driver.id, "approve")} disabled={actionLoading} className="w-full bg-[#10B981] text-white hover:bg-[#059669] font-semibold" data-testid="modal-approve-btn">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />} Approuver
                </Button>
                <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Raison du refus (optionnel)" className="h-9 border-[#CBD5E1]" data-testid="reject-reason-input" />
                <Button onClick={() => onValidate(driver.id, "reject")} disabled={actionLoading} variant="outline" className="w-full border-[#EF4444] text-[#EF4444]" data-testid="modal-reject-btn">
                  <XCircle className="w-4 h-4 mr-1" /> Refuser
                </Button>
              </div>
            ) : (
              <div className="border-t border-[#CBD5E1] pt-4">
                <div className="bg-[#10B981]/10 rounded-lg p-3 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#10B981]" />
                  <p className="text-sm font-medium text-[#0A2540]">Ce chauffeur est approuve</p>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TFTDialog({ driver, onClose, onRefresh }) {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const handleAction = async (type) => {
    if (!driver || !amount || parseInt(amount) <= 0) return;
    setLoading(true);
    try {
      const endpoint = type === "recharge" ? `/admin/tft/recharge/${driver.id}` : `/admin/tft/debit/${driver.id}`;
      const body = type === "recharge" ? { amount: parseInt(amount) } : { amount: parseInt(amount), reason: "Retrait administratif" };
      await api.post(endpoint, body);
      onClose();
      setAmount("");
      onRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const close = () => { onClose(); setAmount(""); setShowHistory(false); };

  return (
    <Dialog open={!!driver} onOpenChange={close}>
      <DialogContent className={showHistory ? "sm:max-w-3xl max-h-[85vh] overflow-y-auto" : "sm:max-w-sm"} data-testid="tft-recharge-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{showHistory ? `Historique TFT - ${driver?.name || ''}` : 'Gestion TFT'}</DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            {showHistory ? "Mouvements du crédit TFT" : "Créditer ou retirer du crédit TFT"}
          </DialogDescription>
        </DialogHeader>
        {driver && !showHistory && (
          <div className="space-y-4">
            <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#CBD5E1]">
              <p className="text-xs text-[#64748B]">Chauffeur</p>
              <p className="text-sm font-semibold text-[#0A2540]">{driver.name}</p>
              <p className="text-xs text-[#64748B]">Solde actuel: <span className={`font-bold ${(driver.tft_balance || 0) > 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>{(driver.tft_balance || 0).toLocaleString()} TFT</span></p>
            </div>
            <div>
              <label className="text-xs font-medium text-[#0A2540]">Montant (TFT)</label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Ex: 5000" min={1} className="h-9 border-[#CBD5E1] mt-1" data-testid="tft-amount-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => handleAction("recharge")} disabled={loading || !amount || parseInt(amount) <= 0} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-semibold" data-testid="confirm-tft-recharge">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Coins className="w-4 h-4 mr-1" />} Crediter
              </Button>
              <Button onClick={() => handleAction("debit")} disabled={loading || !amount || parseInt(amount) <= 0} variant="outline" className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 font-semibold" data-testid="confirm-tft-debit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Coins className="w-4 h-4 mr-1" />} Retirer
              </Button>
            </div>
            <Button onClick={() => setShowHistory(true)} variant="outline" className="w-full border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/5 font-semibold" data-testid="view-tft-history-btn">
              <History className="w-4 h-4 mr-2" /> Voir l&apos;historique des mouvements
            </Button>
          </div>
        )}
        {driver && showHistory && (
          <div className="space-y-3">
            <Button onClick={() => setShowHistory(false)} size="sm" variant="ghost" className="text-[#0A2540] -ml-2" data-testid="back-from-tft-history">
              ← Retour
            </Button>
            <TFTHistory endpoint={`/admin/tft/history/${driver.id}`} currentBalance={driver.tft_balance || 0} title="" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function EditDriverDialog({ driver, onClose, onRefresh }) {
  const [tab, setTab] = useState("profile");
  const [form, setForm] = useState({ name: "", phone: "", email: "", cni_number: "", license_number: "" });
  const [loading, setLoading] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const photoRef = useRef(null);
  const lastIdRef = useRef(null);

  useEffect(() => {
    if (driver && driver.id !== lastIdRef.current) {
      lastIdRef.current = driver.id;
      setForm({
        name: driver.name || "",
        phone: driver.phone || "",
        email: driver.email || "",
        cni_number: driver.cni_number || "",
        license_number: driver.license_number || "",
      });
      setTab("profile");
    } else if (!driver) {
      lastIdRef.current = null;
    }
  }, [driver]);

  const fetchVehicles = async () => {
    if (!driver) return;
    setVehiclesLoading(true);
    try {
      const { data } = await api.get(`/admin/drivers/${driver.id}/vehicles`);
      setVehicles(data || []);
    } catch (e) {
      setVehicles([]);
    } finally { setVehiclesLoading(false); }
  };

  useEffect(() => {
    if (!driver || tab !== "vehicles") return undefined;
    let alive = true;
    setVehiclesLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/admin/drivers/${driver.id}/vehicles`);
        if (alive) setVehicles(data || []);
      } catch (e) {
        if (alive) setVehicles([]);
      } finally {
        if (alive) setVehiclesLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [driver, tab]);

  const saveProfile = async () => {
    if (!driver) return;
    setLoading(true);
    try {
      await api.put(`/admin/drivers/${driver.id}/info`, form);
      onRefresh?.();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !driver) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/admin/drivers/${driver.id}/profile-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Echec upload");
    } finally {
      setPhotoBusy(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const deleteVehicle = async (vehicleId, brand, model) => {
    if (!window.confirm(`Supprimer le véhicule ${brand} ${model} ?`)) return;
    try {
      await api.delete(`/admin/vehicles/${vehicleId}`);
      fetchVehicles();
      onRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={!!driver} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-driver-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0A2540] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Edit2 className="w-5 h-5" /> Édition complète du chauffeur
          </DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            {driver?.name} — profil, documents et véhicules
          </DialogDescription>
        </DialogHeader>
        {driver && (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-3 mb-4" data-testid="edit-driver-tabs">
              <TabsTrigger value="profile" data-testid="edit-driver-tab-profile"><User className="w-3.5 h-3.5 mr-1" /> Profil</TabsTrigger>
              <TabsTrigger value="documents" data-testid="edit-driver-tab-documents"><IdCard className="w-3.5 h-3.5 mr-1" /> Documents</TabsTrigger>
              <TabsTrigger value="vehicles" data-testid="edit-driver-tab-vehicles"><Car className="w-3.5 h-3.5 mr-1" /> Véhicules</TabsTrigger>
            </TabsList>

            {/* PROFIL */}
            <TabsContent value="profile" className="space-y-3">
              <div className="flex items-center gap-4 p-3 rounded-lg bg-[#F8FAFC] border border-[#CBD5E1]">
                {driver.profile_photo ? (
                  <img src={getAuthImgUrl(driver.profile_photo)} alt="profil" className="w-20 h-20 rounded-full object-cover border-2 border-[#FACC15]" data-testid="edit-driver-photo-preview" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-[#0A2540]/10 flex items-center justify-center"><User className="w-8 h-8 text-[#64748B]" /></div>
                )}
                <div>
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="edit-driver-photo-input" />
                  <Button type="button" size="sm" variant="outline" onClick={() => photoRef.current?.click()} disabled={photoBusy} className="border-[#CBD5E1] text-[#0A2540]" data-testid="edit-driver-photo-btn">
                    {photoBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Camera className="w-3 h-3 mr-1" />}
                    {driver.profile_photo ? "Remplacer la photo" : "Ajouter une photo"}
                  </Button>
                  <p className="text-[10px] text-[#64748B] mt-1">JPG/PNG, max 5 Mo</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-[#0A2540]">Nom</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-driver-name" /></div>
                <div><label className="text-xs font-medium text-[#0A2540]">Téléphone</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-driver-phone" /></div>
              </div>
              <div><label className="text-xs font-medium text-[#0A2540]">Email</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-driver-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-[#0A2540]">Numéro CNI</label><Input value={form.cni_number} onChange={(e) => setForm({ ...form, cni_number: e.target.value })} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-driver-cni" /></div>
                <div><label className="text-xs font-medium text-[#0A2540]">Numéro Permis</label><Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-driver-license" /></div>
              </div>
              <Button onClick={saveProfile} disabled={loading} className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e] font-semibold" data-testid="save-driver-edit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Edit2 className="w-4 h-4 mr-1" />} Enregistrer le profil
              </Button>
            </TabsContent>

            {/* DOCUMENTS */}
            <TabsContent value="documents" className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-[#0A2540] uppercase tracking-wide mb-2">CNI</h4>
                <div className="grid grid-cols-2 gap-3">
                  <EditableDocCard label="Recto" docPath={driver?.driver_documents?.cni_front} driverId={driver.id} documentType="cni_front" onChange={onRefresh} />
                  <EditableDocCard label="Verso" docPath={driver?.driver_documents?.cni_back} driverId={driver.id} documentType="cni_back" onChange={onRefresh} />
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-[#0A2540] uppercase tracking-wide mb-2">Permis de Conduire</h4>
                <div className="grid grid-cols-2 gap-3">
                  <EditableDocCard label="Recto" docPath={driver?.driver_documents?.license_front} driverId={driver.id} documentType="license_front" onChange={onRefresh} />
                  <EditableDocCard label="Verso" docPath={driver?.driver_documents?.license_back} driverId={driver.id} documentType="license_back" onChange={onRefresh} />
                </div>
              </div>
            </TabsContent>

            {/* VÉHICULES */}
            <TabsContent value="vehicles" className="space-y-3">
              {vehiclesLoading ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" /></div>
              ) : vehicles.length === 0 ? (
                <div className="py-8 text-center text-[#64748B] bg-[#F8FAFC] rounded-lg border border-dashed border-[#CBD5E1]">
                  <Car className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Ce chauffeur n&apos;a aucun véhicule</p>
                </div>
              ) : (
                <ul className="space-y-2" data-testid="driver-vehicles-list">
                  {vehicles.map((v) => (
                    <li key={v.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#CBD5E1] bg-white hover:border-[#FACC15]/40 transition-colors" data-testid={`driver-vehicle-${v.id}`}>
                      {v.image_url ? (
                        <img src={resolveImageUrl(v.image_url)} alt="" className="w-14 h-14 rounded-md object-cover bg-[#F8FAFC]" />
                      ) : (
                        <div className="w-14 h-14 rounded-md bg-[#F8FAFC] flex items-center justify-center"><Car className="w-6 h-6 text-[#64748B]" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#0A2540] truncate">{v.brand} {v.model} <span className="text-[#64748B] font-normal">({v.year})</span></p>
                        <p className="text-xs text-[#64748B]">{v.plate_number} · {v.city || "?"} · {(v.price_per_day || 0).toLocaleString()} XAF/j</p>
                        <p className="text-[10px] text-[#94A3B8]">{(v.image_urls || []).length} photo(s){v.video_url ? " · 🎥 vidéo" : ""}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => setEditVehicle(v)} className="h-8 px-2 border-[#0A2540] text-[#0A2540]" data-testid={`edit-driver-vehicle-${v.id}`}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteVehicle(v.id, v.brand, v.model)} className="h-8 px-2 border-[#EF4444] text-[#EF4444]" data-testid={`delete-driver-vehicle-${v.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>

      {/* Nested vehicle edit dialog */}
      {editVehicle && (
        <EditVehicleDialog
          vehicle={editVehicle}
          open={!!editVehicle}
          setOpen={(o) => !o && setEditVehicle(null)}
          onUpdated={() => { setEditVehicle(null); fetchVehicles(); onRefresh?.(); }}
        />
      )}
    </Dialog>
  );
}
